import { useEffect } from "react";
import { createPortal } from "react-dom";
import { cx } from "../../lib/utils";
import { Icon } from "../ui/icon";
import { NotificationSettingsPanel } from "./notification-settings-panel";

interface SettingsDialogProps {
  onClose: () => void;
}

// One left-nav entry today ("Notification"), structured as a list so a
// future settings section slots in beside it without restructuring the
// dialog. Each entry owns its own right-panel component.
const SECTIONS = [
  { key: "notification", label: "Notification", icon: "notifications", render: () => <NotificationSettingsPanel /> },
] as const;

// The settings overlay opened from the rail's Settings button — a form-like
// dialog centered over whatever screen is underneath, not a routed page (per
// spec: "a form like ui appears in mid of the screen"). Left panel is
// narrower than the right, matching the spec's two-panel layout; "Notification"
// is selected by default since it's the only section that exists so far.
export function SettingsDialog({ onClose }: SettingsDialogProps) {
  useEffect(() => {
    function onKeyDown(e: KeyboardEvent) {
      if (e.key === "Escape") onClose();
    }
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [onClose]);

  return createPortal(
    <div
      className="fixed inset-0 z-[60] flex items-center justify-center bg-black/40 p-spacing-base"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-label="Settings"
        // Fixed height (not just a max), capped by 85vh on a short viewport —
        // a stable card the recipients list scrolls WITHIN, rather than one
        // that shrink-wraps to content and only starts scrolling once it hits
        // an 85vh ceiling (the original shape: fine with 1-2 recipients, but
        // meant a short browser window could show fewer than 3 rows before
        // scrolling). 30rem comfortably fits the header, the Add-email
        // control, and at least 3 recipient rows before NotificationSettingsPanel's
        // own list scroll area takes over for the rest.
        className="w-full max-w-2xl h-[30rem] max-h-[85vh] bg-surface-container-lowest rounded-2xl shadow-lg overflow-hidden flex flex-col"
      >
        <div className="h-14 shrink-0 px-spacing-lg flex items-center justify-between border-b border-outline-variant/30">
          <h1 className="font-headline-sm text-headline-sm text-on-surface">Settings</h1>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close settings"
            className="w-8 h-8 flex items-center justify-center rounded-lg text-on-surface-variant hover:bg-surface-container hover:text-on-surface"
          >
            <Icon name="close" className="text-[18px]" />
          </button>
        </div>
        <div className="flex flex-1 min-h-0">
          <nav
            aria-label="Settings sections"
            className="w-40 shrink-0 border-r border-outline-variant/30 p-spacing-sm space-y-spacing-3xs overflow-y-auto"
          >
            {SECTIONS.map((s) => (
              <div
                key={s.key}
                aria-current="true"
                className={cx(
                  "flex items-center gap-spacing-xs px-spacing-sm py-spacing-xs rounded-xl font-label-md text-label-md",
                  "bg-primary-container text-on-primary font-medium"
                )}
              >
                <Icon name={s.icon} className="text-[16px]" />
                {s.label}
              </div>
            ))}
          </nav>
          <div className="flex-1 min-w-0 p-spacing-lg overflow-y-auto">{SECTIONS[0].render()}</div>
        </div>
      </div>
    </div>,
    document.body
  );
}
